import dagre from "@dagrejs/dagre";
import type { PipelineEdge, PipelineNode } from "./types";

const NODE_WIDTH = 240;
const NODE_HEIGHT_DEFAULT = 80;
const NODE_HEIGHT_AI = 110;

function getSystemNodeWidth(node: PipelineNode): number {
  const label = String(node.data.label ?? "");
  // Rough text-fit width: icon/status/padding + per-char estimate.
  return Math.max(136, Math.min(210, 92 + label.length * 7));
}

function getNodeDimensions(node: PipelineNode): {
  width: number;
  height: number;
} {
  const data = node.data;
  if (data.nodeType === "ai-check" || data.nodeType === "summary") {
    const checkCount = "checks" in data ? data.checks.length : 1;
    return {
      width: NODE_WIDTH,
      height: Math.max(NODE_HEIGHT_AI, 60 + checkCount * 20),
    };
  }
  if (data.nodeType === "consistency") {
    // Consistency node height is compact — 2 rules visible by default
    const ruleCount = "ruleCount" in data ? (data.ruleCount as number) : 0;
    const visibleRules = Math.min(ruleCount, 2);
    return {
      width: NODE_WIDTH,
      height: Math.max(NODE_HEIGHT_DEFAULT, 48 + visibleRules * 16),
    };
  }
  if (data.nodeType === "system") {
    return { width: getSystemNodeWidth(node), height: NODE_HEIGHT_DEFAULT };
  }
  return { width: NODE_WIDTH, height: NODE_HEIGHT_DEFAULT };
}

export function computeLayout(
  nodes: PipelineNode[],
  edges: PipelineEdge[],
  direction: "TB" | "LR" = "TB",
): {
  nodes: PipelineNode[];
  edges: PipelineEdge[];
  graphHeight: number;
  graphWidth: number;
} {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({
    rankdir: direction,
    nodesep: 40,
    ranksep: 64,
    edgesep: 16,
    marginx: 24,
    marginy: 24,
  });

  // Add padding to dagre dimensions so nodes don't visually overlap
  const PAD = 12;
  for (const node of nodes) {
    const dims = getNodeDimensions(node);
    g.setNode(node.id, {
      width: dims.width + PAD,
      height: dims.height + PAD,
    });
  }
  for (const edge of edges) {
    g.setEdge(edge.source, edge.target);
  }

  dagre.layout(g);

  const layoutedNodes = nodes.map((node) => {
    const dagreNode = g.node(node.id);
    const dims = getNodeDimensions(node);
    return {
      ...node,
      position: {
        x: dagreNode.x - dims.width / 2,
        y: dagreNode.y - dims.height / 2,
      },
    };
  });

  const graphMeta = g.graph();
  const graphHeight = (graphMeta.height ?? 400) + 32;
  const graphWidth = (graphMeta.width ?? 800) + 32;

  return { nodes: layoutedNodes, edges, graphHeight, graphWidth };
}
